import { createTheme, ThemeOptions, Theme } from "@mui/material/styles";

// Rozszerzenie palety o niestandardowe kolory
declare module "@mui/material/styles" {
  interface Palette {
    accent: {
      copper: string;
      steel: string;
      aluminum: string;
      bronze: string;
    };
  }
  interface PaletteOptions {
    accent?: {
      copper?: string;
      steel?: string;
      aluminum?: string;
      bronze?: string;
    };
  }
  interface TypeBackground {
    dark: string;
    gradient: string;
  }
  interface TypeText {
    light: string;
  }
}

// ───────────────────────────────────────────────────────────────────────────────
// Theme palettes
// ───────────────────────────────────────────────────────────────────────────────

export interface ThemePalette {
  /** Display name used in the dev switcher UI. */
  name: string;
  /** Short tagline / character of the palette. */
  description: string;
  primary: { main: string; light: string; dark: string };
  secondary: {
    main: string;
    light: string;
    dark: string;
    contrastText: string;
  };
  background: { default: string; paper: string; dark: string };
  text: { primary: string; secondary: string; light: string };
  /** Optional rgba values used in component overrides (Cards, Tabs, etc.). */
  accents: {
    /** Used for Card hover border, Tab selected indicator, etc. */
    secondaryRgba: string;
    /** Used for divider/border overlays. */
    mutedRgba: string;
  };
}

export const themePalettes = {
  // 1. Granat & Złoto (default — obecny styl industrialny + złoty akcent)
  default: {
    name: "Granat & Złoto",
    description: "Industrialny granat + ciepłe złoto. Domyślny.",
    primary: { main: "#3d5a80", light: "#5b7aa1", dark: "#1a2744" },
    secondary: {
      main: "#d4a853",
      light: "#e4c078",
      dark: "#b8923d",
      contrastText: "#0f1a2e",
    },
    background: { default: "#0a1628", paper: "#0f1f35", dark: "#050d18" },
    text: { primary: "#e8edf5", secondary: "#a0b0c5", light: "#7a8fa8" },
    accents: {
      secondaryRgba: "212, 168, 83",
      mutedRgba: "160, 176, 197",
    },
  },

  // 2. Stalowo-Czerwony (red-blue) — klasyczny industrialny akcent
  redBlue: {
    name: "Stalowo-Czerwony",
    description: "Stalowy granat + intensywna czerwień.",
    primary: { main: "#2d4a6e", light: "#4a6f96", dark: "#1a2c45" },
    secondary: {
      main: "#e63946",
      light: "#f06873",
      dark: "#b3000c",
      contrastText: "#ffffff",
    },
    background: { default: "#0d1929", paper: "#14223a", dark: "#060e1c" },
    text: { primary: "#e8edf5", secondary: "#a0b0c5", light: "#7a8fa8" },
    accents: {
      secondaryRgba: "230, 57, 70",
      mutedRgba: "160, 176, 197",
    },
  },

  // 3. Kuźnia — przemysłowy ogień: grafit + pomarańcz
  forge: {
    name: "Kuźnia",
    description: "Grafit + pomarańczowy żar — surowy przemysł.",
    primary: { main: "#4a4a4a", light: "#6b6b6b", dark: "#2a2a2a" },
    secondary: {
      main: "#ff6b1a",
      light: "#ff8c4a",
      dark: "#cc4a00",
      contrastText: "#1a1a1a",
    },
    background: { default: "#1a1a1a", paper: "#232323", dark: "#0a0a0a" },
    text: { primary: "#f0f0f0", secondary: "#b0b0b0", light: "#888888" },
    accents: {
      secondaryRgba: "255, 107, 26",
      mutedRgba: "176, 176, 176",
    },
  },

  // 4. Recykling — ekologiczny motyw: ciemna zieleń + limonkowy akcent
  eco: {
    name: "Recykling",
    description: "Leśna zieleń + limonkowy akcent — recykling/eco.",
    primary: { main: "#2d5045", light: "#4a7060", dark: "#15302a" },
    secondary: {
      main: "#7cb342",
      light: "#aee571",
      dark: "#4b830d",
      contrastText: "#0d1f17",
    },
    background: { default: "#0d1f17", paper: "#15302a", dark: "#060e0a" },
    text: { primary: "#e8f5ec", secondary: "#a0c5b0", light: "#7a9b8a" },
    accents: {
      secondaryRgba: "124, 179, 66",
      mutedRgba: "160, 197, 176",
    },
  },

  // 5. Czerń & Miedź — premium, elegancki
  copper: {
    name: "Czerń & Miedź",
    description: "Czarny grafit + miedź — premium, elegancki.",
    primary: { main: "#2d2d2d", light: "#4a4a4a", dark: "#1a1a1a" },
    secondary: {
      main: "#b87333",
      light: "#d49460",
      dark: "#8b5a26",
      contrastText: "#1a1a1a",
    },
    background: { default: "#0d0d0d", paper: "#1a1a1a", dark: "#050505" },
    text: { primary: "#ededed", secondary: "#a8a8a8", light: "#888888" },
    accents: {
      secondaryRgba: "184, 115, 51",
      mutedRgba: "168, 168, 168",
    },
  },
} as const satisfies Record<string, ThemePalette>;

export type ThemePaletteKey = keyof typeof themePalettes;

// ───────────────────────────────────────────────────────────────────────────────
// Color utilities — hex ↔ HSL ↔ "r, g, b" CSV
// ───────────────────────────────────────────────────────────────────────────────

const hexToRgb = (hex: string): [number, number, number] => {
  const clean = hex.replace("#", "");
  return [
    parseInt(clean.slice(0, 2), 16),
    parseInt(clean.slice(2, 4), 16),
    parseInt(clean.slice(4, 6), 16),
  ];
};

export const hexToRgbCsv = (hex: string): string => hexToRgb(hex).join(", ");

const rgbToHsl = (r: number, g: number, b: number): [number, number, number] => {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }
  return [h * 360, s * 100, l * 100];
};

const hslToHex = (h: number, s: number, l: number): string => {
  const hue = ((h % 360) + 360) % 360;
  const sat = Math.max(0, Math.min(100, s)) / 100;
  const lit = Math.max(0, Math.min(100, l)) / 100;
  const a = sat * Math.min(lit, 1 - lit);
  const f = (n: number) => {
    const k = (n + hue / 30) % 12;
    const c = lit - a * Math.max(-1, Math.min(k - 3, 9 - k, 1));
    return Math.round(c * 255)
      .toString(16)
      .padStart(2, "0");
  };
  return `#${f(0)}${f(8)}${f(4)}`;
};

/**
 * Apply HSL deltas to a single hex color. Hue is rotated, saturation and
 * lightness are added (clamped to 0-100).
 */
export const adjustHex = (
  hex: string,
  hueShift: number,
  satDelta: number,
  lightDelta: number
): string => {
  const [h, s, l] = rgbToHsl(...hexToRgb(hex));
  return hslToHex(h + hueShift, s + satDelta, l + lightDelta);
};

// ───────────────────────────────────────────────────────────────────────────────
// Palette adjustments — per-color intensity / depth
// ───────────────────────────────────────────────────────────────────────────────

export interface ColorAdjustment {
  /** Saturation delta in percentage points (-50 to 50). Higher = more vivid. */
  saturationDelta: number;
  /** Lightness delta in percentage points (-20 to 20). Negative = deeper. */
  lightnessDelta: number;
}

export interface ThemeAdjustments {
  primary: ColorAdjustment;
  secondary: ColorAdjustment;
}

const zeroAdjustment: ColorAdjustment = {
  saturationDelta: 0,
  lightnessDelta: 0,
};

export const defaultAdjustments: ThemeAdjustments = {
  primary: { ...zeroAdjustment },
  secondary: { ...zeroAdjustment },
};

/**
 * Returns a new palette with primary + secondary colors HSL-tweaked using
 * independent per-color intensity (saturation) and depth (lightness)
 * adjustments. Backgrounds, text and accents are left alone — tweaking them
 * easily breaks contrast on the dark theme.
 */
export const adjustPalette = (
  palette: ThemePalette,
  adj: ThemeAdjustments
): ThemePalette => {
  const tweakPrimary = (hex: string) =>
    adjustHex(hex, 0, adj.primary.saturationDelta, adj.primary.lightnessDelta);
  const tweakSecondary = (hex: string) =>
    adjustHex(hex, 0, adj.secondary.saturationDelta, adj.secondary.lightnessDelta);

  const newSecondaryMain = tweakSecondary(palette.secondary.main);

  return {
    ...palette,
    primary: {
      main: tweakPrimary(palette.primary.main),
      light: tweakPrimary(palette.primary.light),
      dark: tweakPrimary(palette.primary.dark),
    },
    secondary: {
      main: newSecondaryMain,
      light: tweakSecondary(palette.secondary.light),
      dark: tweakSecondary(palette.secondary.dark),
      contrastText: palette.secondary.contrastText,
    },
    accents: {
      ...palette.accents,
      // Recompute the rgba CSV so component overrides stay in sync.
      secondaryRgba: hexToRgbCsv(newSecondaryMain),
    },
  };
};

// ───────────────────────────────────────────────────────────────────────────────
// Theme factory
// ───────────────────────────────────────────────────────────────────────────────

const buildThemeOptions = (p: ThemePalette): ThemeOptions => ({
  palette: {
    mode: "dark",
    primary: {
      main: p.primary.main,
      light: p.primary.light,
      dark: p.primary.dark,
      contrastText: "#ffffff",
    },
    secondary: {
      main: p.secondary.main,
      light: p.secondary.light,
      dark: p.secondary.dark,
      contrastText: p.secondary.contrastText,
    },
    accent: {
      copper: "#b87333",
      steel: "#71797E",
      aluminum: "#A8A9AD",
      bronze: "#CD7F32",
    },
    background: {
      default: p.background.default,
      paper: p.background.paper,
      dark: p.background.dark,
      gradient: `linear-gradient(135deg, ${p.background.default} 0%, ${p.background.paper} 50%, ${p.background.default} 100%)`,
    },
    text: {
      primary: p.text.primary,
      secondary: p.text.secondary,
      light: p.text.light,
    },
    success: { main: "#4ade80", light: "#86efac" },
    warning: { main: "#fbbf24", light: "#fcd34d" },
    error: { main: "#f87171", light: "#fca5a5" },
    divider: `rgba(${p.accents.mutedRgba}, 0.15)`,
  },
  typography: {
    fontFamily: '"Montserrat", "Helvetica Neue", Arial, sans-serif',
    h1: {
      fontFamily: '"Montserrat", sans-serif',
      fontWeight: 800,
      fontSize: "3.5rem",
      lineHeight: 1.1,
      letterSpacing: "-0.02em",
      textTransform: "none",
    },
    h2: {
      fontFamily: '"Montserrat", sans-serif',
      fontWeight: 700,
      fontSize: "2.5rem",
      lineHeight: 1.2,
      letterSpacing: "-0.01em",
    },
    h3: {
      fontFamily: '"Montserrat", sans-serif',
      fontWeight: 700,
      fontSize: "1.875rem",
      lineHeight: 1.25,
    },
    h4: {
      fontFamily: '"Montserrat", sans-serif',
      fontWeight: 600,
      fontSize: "1.5rem",
      lineHeight: 1.35,
    },
    h5: {
      fontFamily: '"Montserrat", sans-serif',
      fontWeight: 600,
      fontSize: "1.25rem",
      lineHeight: 1.4,
    },
    h6: {
      fontFamily: '"Montserrat", sans-serif',
      fontWeight: 600,
      fontSize: "1.125rem",
      lineHeight: 1.45,
    },
    subtitle1: { fontSize: "1.125rem", lineHeight: 1.6, fontWeight: 400 },
    subtitle2: { fontSize: "1rem", lineHeight: 1.5, fontWeight: 500 },
    body1: { fontSize: "1rem", lineHeight: 1.7 },
    body2: { fontSize: "0.875rem", lineHeight: 1.6 },
    button: {
      fontWeight: 600,
      textTransform: "none",
      letterSpacing: "0.02em",
    },
    overline: {
      fontSize: "0.75rem",
      fontWeight: 700,
      letterSpacing: "0.15em",
      textTransform: "uppercase",
    },
  },
  shape: { borderRadius: 8 },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          padding: "12px 28px",
          fontSize: "0.9375rem",
          boxShadow: "none",
          "&:hover": {
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
          },
        },
        contained: { "&:hover": { transform: "translateY(-1px)" } },
        containedPrimary: {
          background: `linear-gradient(135deg, ${p.primary.main} 0%, ${p.primary.light} 100%)`,
          "&:hover": {
            background: `linear-gradient(135deg, ${p.primary.light} 0%, ${p.primary.main} 100%)`,
          },
        },
        containedSecondary: {
          color: p.secondary.contrastText,
          "&:hover": { backgroundColor: p.secondary.light },
        },
        outlined: {
          borderWidth: 2,
          borderColor: `rgba(${p.accents.mutedRgba}, 0.4)`,
          "&:hover": {
            borderWidth: 2,
            borderColor: `rgba(${p.accents.mutedRgba}, 0.7)`,
            backgroundColor: `rgba(${p.accents.mutedRgba}, 0.08)`,
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: "none", backgroundColor: p.background.paper },
        elevation1: { boxShadow: "0 2px 8px rgba(0, 0, 0, 0.25)" },
        elevation2: { boxShadow: "0 4px 16px rgba(0, 0, 0, 0.3)" },
        elevation3: { boxShadow: "0 8px 24px rgba(0, 0, 0, 0.35)" },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: `1px solid rgba(${p.accents.mutedRgba}, 0.15)`,
          backgroundColor: p.background.paper,
          transition: "all 0.3s ease",
          "&:hover": {
            transform: "translateY(-4px)",
            boxShadow: "0 12px 32px rgba(0, 0, 0, 0.4)",
            borderColor: `rgba(${p.accents.secondaryRgba}, 0.3)`,
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 500 },
        filled: {
          "&.MuiChip-colorSecondary": {
            backgroundColor: p.secondary.main,
            color: p.secondary.contrastText,
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
          backgroundColor: p.background.default,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          fontSize: "0.875rem",
          minHeight: 48,
          color: p.text.secondary,
          "&.Mui-selected": { fontWeight: 600, color: p.secondary.main },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          height: 3,
          borderRadius: "3px 3px 0 0",
          backgroundColor: p.secondary.main,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: 8,
            backgroundColor: `rgba(${p.accents.mutedRgba}, 0.05)`,
            "& fieldset": {
              borderColor: `rgba(${p.accents.mutedRgba}, 0.25)`,
            },
            "&:hover fieldset": {
              borderColor: `rgba(${p.accents.mutedRgba}, 0.4)`,
            },
            "&.Mui-focused fieldset": { borderColor: p.secondary.main },
          },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: { borderColor: `rgba(${p.accents.mutedRgba}, 0.15)` },
      },
    },
    MuiDrawer: {
      styleOverrides: { paper: { backgroundColor: p.background.default } },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          "&.Mui-selected": {
            backgroundColor: `rgba(${p.accents.secondaryRgba}, 0.15)`,
          },
          "&:hover": {
            backgroundColor: `rgba(${p.accents.mutedRgba}, 0.08)`,
          },
        },
      },
    },
  },
});

export const createAppTheme = (
  paletteKey: ThemePaletteKey,
  adjustments: ThemeAdjustments = defaultAdjustments
): Theme => {
  const basePalette = themePalettes[paletteKey];
  const finalPalette = adjustPalette(basePalette, adjustments);
  return createTheme(buildThemeOptions(finalPalette));
};

// Default theme (kept as default export for backward compatibility).
const theme = createAppTheme("default");

export default theme;
